import styled, { css, DefaultTheme } from 'styled-components/macro';
import { StyledProps } from 'types/styled.types';
import { TextareaProps } from './textarea.types';
import {
  fieldChromeCss,
  FieldSize,
  FieldStatus,
  sizeConfig,
} from '../_core/field';

type ContainerProps = StyledProps<Pick<TextareaProps, 'size'>> & {
  $status?: FieldStatus;
};

type TextareaElProps = ContainerProps & StyledProps<{ autoResize?: boolean }>;

type SizedProps = StyledProps<Pick<TextareaProps, 'size'>>;

const MIN_HEIGHT = 80;

const typographyCss = (theme: DefaultTheme, size: FieldSize) => {
  const { fontSize, lineHeight, paddingInline } = sizeConfig(theme)[size];

  return css`
    box-sizing: border-box;
    padding-inline: ${paddingInline};
    padding-block: ${theme.spacings.xs};
    border: none;
    white-space: pre-wrap;
    overflow-wrap: break-word;
    font-family: inherit;
    font-size: ${fontSize};
    line-height: ${lineHeight};
  `;
};

export const Root = styled.span`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings.xxs};
    width: 100%;
  `}
`;

export const TextareaWrapper = styled.span<ContainerProps>`
  ${({ theme, $size, $status }) => {
    const { borderRadius, fontSize, lineHeight } = sizeConfig(theme)[$size];

    return css`
      ${fieldChromeCss(theme, $status)}
      flex-direction: column;
      border-radius: ${borderRadius};
      font-size: ${fontSize};
      line-height: ${lineHeight};
      overflow: hidden;
    `;
  }}
`;

export const AutoSizer = styled.span`
  display: grid;
  width: 100%;

  > * {
    grid-area: 1 / 1 / 2 / 2;
  }
`;

export const Mirror = styled.span<SizedProps>`
  ${({ theme, $size }) => css`
    ${typographyCss(theme, $size)}

    min-height: ${MIN_HEIGHT}px;
    visibility: hidden;
    pointer-events: none;
  `}
`;

export const StyledTextarea = styled.textarea<TextareaElProps>`
  ${({ theme, $size, $autoResize }) => {
    return css`
      ${typographyCss(theme, $size)}
      color: ${theme.semantic.text.default};
      background-color: transparent;
      outline: none;
      resize: ${$autoResize ? 'none' : 'vertical'};
      width: 100%;
      min-height: ${MIN_HEIGHT}px;
      transition: color ${theme.tokens.animation.duration.base};

      ${$autoResize &&
      css`
        overflow-y: hidden;
        /*
         * !important здесь важен: нативный resize записывает height прямо
         * в style элемента, и эта запись переживает переключение autoResize.
         * Инлайн иначе не перебить.
         */
        height: 100% !important;
      `}

      &::placeholder {
        color: ${theme.semantic.text.placeholder};
      }

      &:disabled {
        cursor: not-allowed;
        resize: none;
        color: ${theme.semantic.text.disabled};
        -webkit-text-fill-color: ${theme.semantic.text.disabled};
      }
    `;
  }}
`;

export const TextareaFooter = styled.span`
  display: flex;
  justify-content: flex-end;
`;
